/**
 * The open widget: header, then the `surface` (the choice, the call or the chat),
 * then the action of the moment and the "powered by DeepAgent" mark.
 */

import { LitElement, css, html, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";

import "./deepagent-avatar.js";
import "./deepagent-call-controls.js";
import "./deepagent-call-stage.js";
import "./deepagent-consent.js";
import "./deepagent-chat.js";

import type {
  ConversationState,
  SessionModality,
  TranscriptEntry,
} from "../state/conversation-controller.js";
import type { WidgetSettingsPayload } from "../state/api-client.js";
import { isMobileViewport } from "../utils/browser-checks.js";
import { flyFigure } from "./figure-flight.js";
import {
  LOCALE_LABELS,
  type I18nStrings,
  type SupportedLocale,
} from "../i18n/index.js";
import {
  backIcon,
  callIcon,
  chatIcon,
  chevronDownIcon,
  closeIcon,
} from "./icons.js";

/** Detail of `accept-terms`: the session to open now, or null to let the visitor pick. */
export type AcceptTermsDetail = SessionModality | null;

/**
 * What the panel shows. `choice` exists only on a widget that takes both, until
 * the visitor picks; everything the render decides follows from this value.
 */
type PanelSurface = "choice" | "call" | "chat";

interface PanelBranding {
  name: string;
  avatarUrl: string | null;
}

type PanelEvent =
  | "start-call"
  | "end-call"
  | "end-chat"
  | "mute-toggle"
  | "close-panel"
  | "choose-chat"
  | "back-to-choice";

@customElement("deepagent-panel")
export class DeepAgentPanel extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: var(--da-panel-width, 380px);
      max-width: calc(100vw - 24px);
      height: var(--da-panel-height, 600px);
      max-height: calc(100vh - 24px);
      background: var(--da-bg);
      color: var(--da-text);
      border: 1px solid var(--da-border);
      border-radius: var(--da-radius-lg, 2px);
      box-shadow: var(--da-shadow-panel);
      overflow: hidden;
      position: relative;
      animation: da-panel-in 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
      font-family: inherit;
      isolation: isolate;
    }

    button {
      font-family: inherit;
      -webkit-tap-highlight-color: transparent;
    }

    /* ── Drag handle (mobile sheet only) ─────────────────── */
    .grabber {
      display: none;
      align-items: center;
      justify-content: center;
      /* Tall enough to grab without aiming; the visible bar is much smaller. */
      height: 20px;
      flex: 0 0 auto;
      cursor: grab;
      touch-action: none;
    }

    .grabber::after {
      content: "";
      width: 36px;
      height: 4px;
      border-radius: 2px;
      background: var(--da-border-strong);
    }

    /* ── Header ─────────────────────────────────────────── */
    header {
      display: flex;
      align-items: center;
      gap: 10px;
      min-height: 60px;
      padding: 12px 12px 8px 16px;
      flex: 0 0 auto;
    }

    header deepagent-avatar {
      --da-avatar-size: 36px;
    }

    .name-block {
      flex: 1 1 auto;
      min-width: 0;
    }

    .name {
      font-family: var(--da-font-display);
      font-weight: 600;
      font-size: 15px;
      letter-spacing: -0.015em;
      color: var(--da-text);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .status {
      font-size: 12px;
      color: var(--da-text-muted);
      margin-top: 2px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--da-online);
    }

    .status[data-state="connecting"] .status-dot,
    .status[data-state="requesting_mic"] .status-dot {
      background: var(--da-text-muted);
      animation: da-dot-pulse 1.2s ease-in-out infinite;
    }

    .status[data-state="active"] .status-dot {
      background: var(--da-accent);
      animation: da-heartbeat 1.6s ease-out infinite;
    }

    .status[data-state="ended"] .status-dot,
    .status[data-state="error"] .status-dot {
      background: var(--da-text-dim);
    }

    .icon-button {
      appearance: none;
      flex: 0 0 auto;
      width: 32px;
      height: 32px;
      padding: 0;
      border: none;
      border-radius: var(--da-radius, 2px);
      background: transparent;
      color: var(--da-text-muted);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition:
        background-color 180ms cubic-bezier(0.16, 1, 0.3, 1),
        color 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    @media (hover: hover) {
      .icon-button:hover {
        background: var(--da-surface-2);
        color: var(--da-text);
      }
    }

    .icon-button svg {
      width: 16px;
      height: 16px;
    }

    .back {
      margin-left: -8px;
    }

    /* ── Locale picker ──────────────────────────────────────
       A code chip (IT / EN / ES), never a full-width row. The native <select>
       is layered on top at zero opacity so we keep the OS picker (and its
       keyboard and screen-reader behaviour) without inheriting its chrome.
    ─────────────────────────────────────────────────────── */
    .locale {
      position: relative;
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
    }

    .locale-chip {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      height: 32px;
      padding: 0 6px 0 8px;
      border-radius: var(--da-radius, 2px);
      color: var(--da-text-muted);
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      pointer-events: none;
      transition: background-color 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    .locale-chip svg {
      width: 12px;
      height: 12px;
    }

    .locale select {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
      font-family: inherit;
      appearance: none;
      -webkit-appearance: none;
      border: none;
      background: transparent;
    }

    .locale select:focus-visible + .locale-chip {
      outline: 2px solid var(--da-accent);
      outline-offset: 2px;
    }

    @media (hover: hover) {
      .locale:hover .locale-chip {
        background: var(--da-surface-2);
        color: var(--da-text);
      }
    }

    /* Ending a chat is a small, quiet exit next to the close button, not a
       full-width button competing with the composer for the last word. */
    .end-chat {
      appearance: none;
      border: none;
      background: none;
      padding: 0 4px;
      height: 32px;
      color: var(--da-text-muted);
      font-size: 13px;
      text-decoration: underline;
      text-underline-offset: 3px;
      cursor: pointer;
      flex: 0 0 auto;
    }

    @media (hover: hover) {
      .end-chat:hover {
        color: var(--da-text);
      }
    }

    button:focus-visible {
      outline: 2px solid var(--da-accent);
      outline-offset: 2px;
    }

    /* ── Error ──────────────────────────────────────────── */
    .error {
      margin: 12px 16px 0;
      padding: 10px 12px;
      background: var(--da-danger-bg);
      color: var(--da-text);
      font-size: 13px;
      line-height: 1.45;
      border-radius: var(--da-radius-lg, 2px);
      flex: 0 0 auto;
    }

    /* ── Choice ─────────────────────────────────────────── */
    .choice {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 32px;
      padding: 24px 28px 32px;
      text-align: center;
      overscroll-behavior: contain;
    }

    /* Centred while it fits; scrollable from the top, never cut off, once it does not. */
    .choice > :first-child { margin-top: auto; }
    .choice > :last-child { margin-bottom: auto; }

    .greeting {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 6;
      overflow: hidden;
      margin: 0;
      max-width: 290px;
      font-family: var(--da-font-display);
      font-size: 18px;
      font-weight: 500;
      line-height: 1.4;
      letter-spacing: -0.015em;
      color: var(--da-text);
      text-wrap: balance;
    }

    /* The agent is the call button too: pressing the figure calls it. The pill
       under it is the named, focusable version of the same action. */
    .orb-call {
      appearance: none;
      border: none;
      background: none;
      padding: 0;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      color: var(--da-text);
      font-size: 15px;
      font-weight: 600;
      letter-spacing: -0.01em;
      --da-avatar-size: 120px;
    }

    .orb-call deepagent-avatar {
      transition: transform 260ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    @media (hover: hover) {
      .orb-call:hover:not(:disabled) deepagent-avatar {
        transform: scale(1.04);
      }
    }

    .orb-call:active:not(:disabled) deepagent-avatar {
      transform: scale(0.97);
      transition-duration: 90ms;
    }

    .orb-call:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .pair {
      display: flex;
      gap: 8px;
      margin-top: -8px;
    }

    .pill {
      appearance: none;
      border: none;
      cursor: pointer;
      height: 40px;
      padding: 0 20px;
      border-radius: var(--da-radius, 2px);
      font: inherit;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: -0.005em;
      transition:
        background-color 180ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    .pill:active:not(:disabled) {
      transform: scale(0.98);
      transition-duration: 90ms;
    }

    .pill.primary {
      background: var(--da-accent);
      color: var(--da-accent-fg);
    }

    @media (hover: hover) {
      .pill.primary:hover:not(:disabled) {
        background: color-mix(in srgb, var(--da-accent) 88%, #000);
      }
    }

    .pill.secondary {
      background: color-mix(in srgb, var(--da-accent) 12%, transparent);
      color: var(--da-text);
    }

    @media (hover: hover) {
      .pill.secondary:hover:not(:disabled) {
        background: color-mix(in srgb, var(--da-accent) 18%, transparent);
      }
    }

    .pill:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    /* ── Actions: the two ways to talk ──────────────────── */
    .actions {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
    }

    .footer-actions {
      padding: 12px 16px 16px;
      flex: 0 0 auto;
    }

    .footer-actions .actions {
      flex-direction: row;
    }

    .action {
      appearance: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      min-height: 52px;
      padding: 8px 16px;
      border-radius: var(--da-radius, 2px);
      text-align: left;
      transition:
        background-color 180ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    .footer-actions .action {
      justify-content: center;
      min-height: 48px;
    }

    .action:active:not(:disabled) {
      transform: scale(0.98);
      transition-duration: 90ms;
    }

    .action svg {
      width: 20px;
      height: 20px;
      flex: 0 0 auto;
    }


    .action-label {
      font-size: 15px;
      font-weight: 600;
      letter-spacing: -0.01em;
    }


    .action.primary {
      border: 1px solid transparent;
      background: var(--da-accent);
      color: var(--da-accent-fg);
    }

    @media (hover: hover) {
      .action.primary:hover:not(:disabled) {
        background: color-mix(in srgb, var(--da-accent) 88%, #000);
      }
    }

    .action.secondary {
      border: 1px solid var(--da-input);
      background: transparent;
      color: var(--da-text);
    }

    @media (hover: hover) {
      .action.secondary:hover:not(:disabled) {
        background: var(--da-surface-2);
      }
    }

    .action:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    /* ── Powered by ─────────────────────────────────────── */
    .powered {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      font-size: 11px;
      color: var(--da-text-muted);
      padding: 0 0 12px;
      flex: 0 0 auto;
      text-decoration: none;
    }

    .powered svg {
      width: 12px;
      height: 12px;
      border-radius: 2px;
      overflow: hidden;
    }

    /* ── Animations ─────────────────────────────────────── */
    @keyframes da-panel-in {
      from { transform: translateY(6px); opacity: 0; }
      to   { transform: translateY(0);   opacity: 1; }
    }

    @keyframes da-dot-pulse {
      0%, 100% { opacity: 1; }
      50%      { opacity: 0.35; }
    }

    /* Live is a two-beat pulse, not a radar ping. */
    @keyframes da-heartbeat {
      0%, 18%, 100% { transform: scale(1); }
      6%, 24%       { transform: scale(1.5); }
    }

    @keyframes da-sheet-in {
      from { transform: translateY(100%); }
      to   { transform: translateY(0); }
    }

    /* Sentinels exist only to catch focus leaving the panel; they must never
       be seen, and must never take space. */
    .sentinel {
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
    }

    @media (max-height: 640px) {
      .choice { gap: 24px; }
      .orb-call { --da-avatar-size: 96px; }
      .greeting { font-size: 16px; -webkit-line-clamp: 4; }
    }

    /* A fingertip needs 44px; the header keeps its compact look for a mouse. */
    @media (pointer: coarse) {
      .icon-button { width: 44px; height: 44px; }
      .locale-chip, .end-chat { height: 44px; }
      .pill { height: 48px; padding: 0 24px; font-size: 15px; }
    }

    /* ── Mobile sheet ─────────────────────────────────────────────────────
       Fills the host rather than the viewport. The host owns the safe-area
       padding, so 100vw/100vh would overflow it by exactly that inset.
    ─────────────────────────────────────────────────────────────────────── */
    @media (max-width: 640px) {
      :host {
        width: 100%;
        max-width: 100%;
        height: 100%;
        max-height: 100%;
        border: none;
        animation: da-sheet-in 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
        transform: translateY(var(--da-sheet-drag, 0px));
        transition: transform 240ms cubic-bezier(0.16, 1, 0.3, 1);
      }

      header {
        touch-action: none;
      }

      /* Mid-drag the transform belongs to the finger, not to a transition. */
      :host([data-dragging]) {
        transition: none;
      }

      .grabber { display: flex; }
    }

    /* Played once: a sheet let go after a drag settles, it does not slide in again. */
    :host([data-entered]) { animation: none; }

    @media (prefers-reduced-motion: reduce) {
      :host { animation: none; transition: none; }
      .status-dot { animation: none !important; }
    }
  `;

  @property({ type: Object }) branding: PanelBranding | null = null;
  @property({ type: String }) avatarUrl: string | null = null;
  @property({ type: String }) state: ConversationState = "idle";
  @property({ type: Boolean }) isMicMuted = false;
  @property({ type: String }) agentMode: "speaking" | "listening" | "idle" = "idle";
  @property({ type: Array }) transcript: TranscriptEntry[] = [];
  @property({ type: String }) errorMessage: string | null = null;
  @property({ type: Object }) strings!: I18nStrings;
  @property({ type: Boolean }) showTranscript = true;
  /** What the owner allows the visitor to start. */
  @property({ type: String }) modality: WidgetSettingsPayload["modality"] = "voice";
  /**
   * Modality of the running or just-finished session; null before one opens.
   * Kept through `ended` so the outcome reads as a chat or a call.
   */
  @property({ type: String }) sessionModality: SessionModality | null = null;
  /** On a widget that takes both: the visitor picked writing. */
  @property({ type: Boolean }) chatChosen = false;
  /** True between a typed message and the agent's written reply. */
  @property({ type: Boolean }) awaitingReply = false;
  /** What is in the composer. Passed through; the host owns it. */
  @property({ type: String }) draft = "";
  /** "Powered by DeepAgent" footer. Owner-controlled, defaults on. */
  @property({ type: Boolean }) showBranding = true;
  /** False when the owner forbade minimising — the close affordance disappears. */
  @property({ type: Boolean }) canClose = true;
  /** Terms the visitor must accept before a call may start; null when none. */
  @property({ type: String }) termsContent: string | null = null;
  @property({ type: String }) acceptTermsLabel = "";
  @property({ type: String }) dismissTermsLabel = "";
  /** Locales the picker may offer. Fewer than two hides it entirely. */
  @property({ type: Array }) locales: SupportedLocale[] = [];
  @property({ type: String }) locale: SupportedLocale = "en";

  /** Callbacks to pull live FFT data from the conversation controller. */
  @property({ attribute: false })
  outputFreqData: (() => Uint8Array | null) | null = null;
  @property({ attribute: false })
  inputFreqData: (() => Uint8Array | null) | null = null;

  /** Drag distance past which releasing the sheet dismisses it. */
  private static readonly DISMISS_THRESHOLD_PX = 90;
  /** A flick this fast (px/ms) dismisses however short it was. */
  private static readonly DISMISS_VELOCITY = 0.5;

  private drag: {
    pointerId: number;
    startY: number;
    delta: number;
    /** The last moves, to read the release speed from the end of the gesture. */
    samples: { y: number; t: number }[];
  } | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("animationend", this.markEntered);
  }

  override disconnectedCallback(): void {
    this.removeEventListener("animationend", this.markEntered);
    super.disconnectedCallback();
  }

  private markEntered = (event: AnimationEvent): void => {
    if (event.target === this) this.toggleAttribute("data-entered", true);
  };

  private lastSurface: PanelSurface | null = null;
  private flightFrom: DOMRect | null = null;

  /** The agent's figure on the current surface, once it is laid out. */
  async figure(): Promise<HTMLElement | null> {
    await this.updateComplete;
    const root = this.renderRoot;
    if (this.surface === "choice")
      return root.querySelector<HTMLElement>(".orb-call deepagent-avatar");
    if (this.surface === "call") {
      const stage = root.querySelector("deepagent-call-stage");
      if (!stage) return null;
      await stage.updateComplete;
      return stage.renderRoot.querySelector<HTMLElement>("deepagent-matrix");
    }
    return root.querySelector<HTMLElement>("header deepagent-avatar");
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    super.willUpdate(changed);
    // Pressing the orb to call: the same figure grows into the call stage.
    if (this.lastSurface === "choice" && this.surface === "call") {
      this.flightFrom =
        this.renderRoot
          .querySelector(".orb-call deepagent-avatar")
          ?.getBoundingClientRect() ?? null;
    }
  }

  protected override updated(changed: PropertyValues<this>): void {
    super.updated(changed);
    this.lastSurface = this.surface;
    const from = this.flightFrom;
    if (!from) return;
    this.flightFrom = null;
    void this.figure().then((target) => {
      if (target)
        flyFigure({
          from,
          target,
          avatarUrl: this.avatarUrl,
          layer: this.renderRoot as ShadowRoot,
          frame: this,
        });
    });
  }

  /**
   * Move focus to whatever the visitor most likely opened the panel to press.
   *
   * Called by the host once the panel is on screen, so a keyboard or screen
   * reader user lands on the action rather than having to tab in from the page.
   */
  focusPrimary(): void {
    const root = this.renderRoot as ShadowRoot;
    // The consent gate blocks everything else, so it comes first.
    const consent = root.querySelector("deepagent-consent");
    if (consent) {
      void consent.focusAccept();
      return;
    }
    if (this.surface === "chat") {
      const chat = root.querySelector("deepagent-chat");
      if (chat) {
        void chat.focusComposer();
        return;
      }
    }
    const target =
      root.querySelector<HTMLElement>(".pill.primary:not(:disabled)") ??
      root.querySelector<HTMLElement>(".action.primary:not(:disabled)") ??
      root.querySelector<HTMLElement>("deepagent-call-controls") ??
      root.querySelector<HTMLElement>(".close");
    target?.focus();
  }

  /**
   * One modality is its own surface. A widget that takes both opens on the
   * choice until the visitor picks: a call takes the panel as soon as it
   * starts, a chat as soon as they choose to write.
   */
  get surface(): PanelSurface {
    if (this.modality === "voice") return "call";
    if (this.modality === "text") return "chat";
    if (this.sessionModality === "voice") return "call";
    if (this.sessionModality === "text" || this.chatChosen) return "chat";
    return this.state === "connecting" || this.state === "requesting_mic"
      ? "call"
      : "choice";
  }

  /**
   * True when the panel is a full-screen sheet rather than a corner card.
   *
   * Decides whether focus is trapped. A trap belongs to a modal: on mobile the
   * sheet covers the page and locks its scroll, so it is one. On desktop the
   * panel sits in a corner of a page the visitor is still reading, and trapping
   * focus there would strand keyboard users inside a widget they only wanted to
   * tab past.
   *
   * Reads the same 640px breakpoint the stylesheet does, so the behaviour and
   * the layout can never disagree.
   */
  private get isSheet(): boolean {
    return isMobileViewport();
  }

  private focusFirst = (): void => {
    const root = this.renderRoot as ShadowRoot;
    (
      root.querySelector<HTMLElement>(".back") ??
      root.querySelector<HTMLElement>(".locale select") ??
      root.querySelector<HTMLElement>(".close")
    )?.focus();
  };

  private handleGrabberDown = (event: PointerEvent): void => {
    if (!this.canClose || !this.isSheet || this.drag) return;
    // The header is a handle too, except for its own buttons and picker.
    if ((event.target as Element).closest("button, select")) return;
    this.drag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      delta: 0,
      samples: [{ y: event.clientY, t: event.timeStamp }],
    };
    this.toggleAttribute("data-dragging", true);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  };

  private handleGrabberMove = (event: PointerEvent): void => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const raw = event.clientY - drag.startY;
    // Up past the top edge the sheet resists instead of revealing the page behind it.
    drag.delta = raw >= 0 ? raw : -24 * (1 - 1 / (1 + -raw / 120));
    drag.samples = [...drag.samples, { y: event.clientY, t: event.timeStamp }].slice(-5);
    // Straight to the custom property, so following a finger never re-renders the panel.
    this.style.setProperty("--da-sheet-drag", `${drag.delta}px`);
  };

  private handleGrabberUp = (event: PointerEvent): void => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    this.drag = null;
    const first = drag.samples[0];
    const last = drag.samples[drag.samples.length - 1];
    const elapsed = first && last ? last.t - first.t : 0;
    // A flick held still before letting go is no longer a flick.
    const velocity =
      first && last && elapsed > 0 && event.timeStamp - last.t < 100
        ? (last.y - first.y) / elapsed
        : 0;
    // A swipe never hangs up: only the close button and the end button do.
    const live = ["active", "connecting", "requesting_mic"].includes(this.state);
    const dismiss =
      event.type === "pointerup" &&
      !live &&
      (drag.delta > DeepAgentPanel.DISMISS_THRESHOLD_PX ||
        (drag.delta > 12 && velocity > DeepAgentPanel.DISMISS_VELOCITY));
    this.toggleAttribute("data-dragging", false);
    const handle = event.currentTarget as HTMLElement;
    if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
    if (!dismiss) {
      // Back to rest along the transition the stylesheet gives the released sheet.
      this.style.removeProperty("--da-sheet-drag");
      return;
    }
    this.style.setProperty("--da-sheet-drag", "100%");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.setTimeout(() => this.emit("close-panel"), reduced ? 0 : 220);
  };

  private emit(name: PanelEvent) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true }));
  }

  /**
   * Consent accepted. The detail is the session the host should open at once,
   * or null when the widget offers both a call and a chat: there the visitor
   * still has to choose, so accepting only unlocks the two buttons.
   */
  private emitAcceptTerms(modality: AcceptTermsDetail) {
    this.dispatchEvent(
      new CustomEvent<AcceptTermsDetail>("accept-terms", {
        detail: modality,
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Only a voice-only widget has one action, so consent and the call are one press. */
  private get acceptTermsAction(): AcceptTermsDetail {
    return this.modality === "voice" ? "voice" : null;
  }

  private get acceptTermsFallbackLabel(): string {
    return this.acceptTermsAction === "voice"
      ? this.strings.startCall
      : this.strings.acceptTerms;
  }

  private emitLocale(locale: SupportedLocale) {
    this.dispatchEvent(
      new CustomEvent("locale-change", {
        detail: locale,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private renderLocalePicker() {
    if (this.locales.length < 2) return null;
    return html`
      <div class="locale">
        <select
          aria-label=${this.strings.changeLanguage}
          .value=${this.locale}
          @change=${(e: Event) =>
            this.emitLocale(
              (e.target as HTMLSelectElement).value as SupportedLocale,
            )}
        >
          ${this.locales.map(
            (l) =>
              html`<option value=${l} ?selected=${l === this.locale}>
                ${LOCALE_LABELS[l]}
              </option>`,
          )}
        </select>
        <span class="locale-chip" aria-hidden="true">
          ${this.locale}${chevronDownIcon()}
        </span>
      </div>
    `;
  }

  /** The agent's opening line, shown on the choice so the visitor reads who answers. */
  private get greeting(): string | null {
    return this.transcript.find((entry) => entry.role === "ai")?.text ?? null;
  }

  private callAction() {
    const isConnecting = this.state === "connecting";
    const isRequestingMic = this.state === "requesting_mic";
    const label = isRequestingMic
      ? this.strings.micPrompt
      : isConnecting
        ? this.strings.connecting
        : this.strings.startCall;
    return html`<button
      type="button"
      class="action primary"
      ?disabled=${isConnecting || isRequestingMic || this.termsContent !== null}
      @click=${() => this.emit("start-call")}
    >
      ${callIcon()}
      <span class="action-label">${label}</span>
    </button>`;
  }

  private chatAction() {
    return html`<button
      type="button"
      class="action secondary"
      ?disabled=${this.termsContent !== null}
      @click=${() => this.emit("choose-chat")}
    >
      ${chatIcon()}
      <span class="action-label">${this.strings.startChat}</span>
    </button>`;
  }

  private renderChoice() {
    const locked = this.termsContent !== null;
    return html`<div class="choice">
      ${this.greeting ? html`<p class="greeting">${this.greeting}</p>` : null}
      <button
        type="button"
        class="orb-call"
        tabindex="-1"
        aria-hidden="true"
        ?disabled=${locked}
        @click=${() => this.emit("start-call")}
      >
        <deepagent-avatar .url=${this.avatarUrl} .cells=${9}></deepagent-avatar>
      </button>
      <div class="pair">
        <button
          type="button"
          class="pill primary"
          ?disabled=${locked}
          @click=${() => this.emit("start-call")}
        >${this.strings.startCall}</button>
        <button
          type="button"
          class="pill secondary"
          ?disabled=${locked}
          @click=${() => this.emit("choose-chat")}
        >${this.strings.startChat}</button>
      </div>
    </div>`;
  }

  /** Under the body: the call controls, the consent gate, or the call button. */
  private renderFooter(surface: PanelSurface) {
    if (this.state === "active" && surface === "call") {
      return html`<deepagent-call-controls
        .muted=${this.isMicMuted}
        .muteLabel=${this.strings.muteMicrophone}
        .unmuteLabel=${this.strings.unmuteMicrophone}
        .endLabel=${this.strings.endCall}
        @mute-toggle=${() => this.emit("mute-toggle")}
        @end-call=${() => this.emit("end-call")}
      ></deepagent-call-controls>`;
    }
    if (this.termsContent) {
      return html`<deepagent-consent
        .content=${this.termsContent}
        .acceptLabel=${this.acceptTermsLabel || this.acceptTermsFallbackLabel}
        .dismissLabel=${this.dismissTermsLabel || this.strings.close}
        @accept=${() => this.emitAcceptTerms(this.acceptTermsAction)}
        @dismiss=${() => this.emit("close-panel")}
      ></deepagent-consent>`;
    }
    if (surface !== "call") return null;
    // After a call on a widget that also writes, both ways stay open.
    const offersChat =
      this.modality === "voice_text" &&
      (this.state === "ended" || this.state === "error");
    return html`<div class="footer-actions">
      <div class="actions">
        ${this.callAction()}
        ${offersChat ? this.chatAction() : null}
      </div>
    </div>`;
  }

  private get statusLabel(): string {
    const s = this.strings;
    const isChat = this.sessionModality === "text";
    switch (this.state) {
      case "requesting_mic": return s.micPrompt;
      case "connecting":     return s.connecting;
      // A chat is not "In call": nobody is waiting on the line, and the word
      // makes a written exchange sound like a call in progress.
      case "active":         return isChat ? s.chatActive : s.connected;
      case "ended":          return isChat ? s.chatEnded : s.callEnded;
      default:               return s.online;
    }
  }

  private get errorLabel(): string | null {
    if (!this.errorMessage) return null;
    const s = this.strings;
    switch (this.errorMessage) {
      case "mic_permission_denied": return s.micPermissionDenied;
      // Told apart because the fix is different every time: change a setting,
      // plug something in, or quit whatever already holds the device.
      case "mic_not_found":         return s.micNotFound;
      case "mic_unavailable":       return s.micUnavailable;
      case "webrtc_unsupported":    return s.webrtcUnsupported;
      // The server returns 409 agent_not_deployed when the agent row exists and
      // is public but was never provisioned. Telling the visitor to check their
      // connection sends them chasing a problem that isn't theirs.
      case "agent_not_deployed":   return s.agentNotDeployed;
      // 403 from the allowlist gate. "Check your connection" sent operators
      // hunting a network fault when the real fix is one field in the builder.
      case "origin_not_allowed":   return s.originNotAllowed;
      case "preview_only":         return s.previewOnly;
      case "missing_dynamic_variables":
      case "invalid_dynamic_variables":
      case "unauthorized_dynamic_variables": return s.variableConfigurationError;
      case "quota_exhausted":
      case "variable_schema_unavailable": return s.unavailable;
      case "rate_limited": return s.rateLimited;
      // 409 from the session start when the bundle asks for a session the agent
      // no longer offers (a cached build against a switched modality). Nothing
      // the visitor can fix, so a neutral message rather than a network fault.
      case "modality_not_allowed": return s.unavailable;
      default:                       return s.networkError;
    }
  }

  override render() {
    const isActive = this.state === "active";
    const surface = this.surface;
    const agentName = this.branding?.name ?? "DeepAgent";
    // A call shows its transcript only once there is one, and only if the
    // owner wants it; the chat surface is the conversation, so always.
    const showsThread =
      surface === "chat" ||
      (surface === "call" &&
        this.showTranscript &&
        (isActive || this.transcript.length > 0));
    // Back to the choice, only on a widget that offers one and while no chat runs.
    const showsBack =
      this.modality === "voice_text" &&
      surface === "chat" &&
      !isActive &&
      this.state !== "connecting";
    // Only trapped when the panel is modal — see `isSheet`.
    const trapIndex = this.isSheet ? "0" : "-1";

    return html`
      <span class="sentinel" tabindex=${trapIndex} @focus=${this.focusPrimary}></span>

      ${this.canClose
        ? html`<div
            class="grabber"
            @pointerdown=${this.handleGrabberDown}
            @pointermove=${this.handleGrabberMove}
            @pointerup=${this.handleGrabberUp}
            @pointercancel=${this.handleGrabberUp}
          ></div>`
        : null}

      <header
        @pointerdown=${this.canClose ? this.handleGrabberDown : null}
        @pointermove=${this.canClose ? this.handleGrabberMove : null}
        @pointerup=${this.canClose ? this.handleGrabberUp : null}
        @pointercancel=${this.canClose ? this.handleGrabberUp : null}
      >
        ${showsBack
          ? html`<button
              type="button"
              class="icon-button back"
              aria-label=${this.strings.back}
              title=${this.strings.back}
              @click=${() => this.emit("back-to-choice")}
            >
              ${backIcon()}
            </button>`
          : null}
        ${surface === "chat"
          ? html`<deepagent-avatar
              .url=${this.avatarUrl}
              .mode=${this.awaitingReply && isActive ? "connecting" : "idle"}
            ></deepagent-avatar>`
          : null}
        <div class="name-block">
          <div class="name">${agentName}</div>
          ${this.state === "idle"
            ? null
            : html`<div class="status" data-state=${this.state}>
                <span class="status-dot" aria-hidden="true"></span>
                <span>${this.statusLabel}</span>
              </div>`}
        </div>
        ${isActive && surface === "chat"
          ? html`<button class="end-chat" @click=${() => this.emit("end-chat")}>
              ${this.strings.endChat}
            </button>`
          : null}
        ${this.renderLocalePicker()}
        ${this.canClose
          ? html`<button
              type="button"
              class="icon-button close"
              @click=${() => this.emit("close-panel")}
              aria-label=${this.strings.close}
              title=${this.strings.close}
            >
              ${closeIcon()}
            </button>`
          : null}
      </header>

      ${this.errorLabel
        ? html`<div class="error" role="alert">${this.errorLabel}</div>`
        : null}

      ${surface === "choice" ? this.renderChoice() : null}

      ${surface === "call"
        ? html`<deepagent-call-stage
            .state=${this.state}
            .agentMode=${this.agentMode}
            .compact=${showsThread}
            .strings=${this.strings}
            .agentName=${agentName}
            .outputFreqData=${this.outputFreqData}
            .inputFreqData=${this.inputFreqData}
          ></deepagent-call-stage>`
        : null}

      ${showsThread
        ? html`<deepagent-chat
            .composer=${surface === "chat"}
            .entries=${this.transcript}
            .draft=${this.draft}
            .awaitingReply=${this.awaitingReply && isActive}
            .disabled=${this.state === "requesting_mic" || this.termsContent !== null}
            .connecting=${this.state === "connecting"}
            .labelUser=${this.strings.transcriptYou}
            .labelAi=${this.strings.transcriptAgent}
            .placeholder=${this.strings.composerPlaceholder}
            .sendLabel=${this.strings.send}
            .typingLabel=${this.strings.agentTyping}
            .hint=${surface === "call" ? this.strings.transcriptHint : this.strings.chatHint}
          ></deepagent-chat>`
        : null}

      ${this.renderFooter(surface)}

      ${this.showBranding
        ? html`<div class="powered">
            <svg viewBox="0 0 250 250" fill="none" aria-hidden="true">
              <defs>
                <linearGradient id="da-favicon-grad" x1="0" y1="1" x2="0" y2="0"
                                gradientUnits="objectBoundingBox">
                  <stop offset="0%"     stop-color="#FFF7BB" />
                  <stop offset="23.79%" stop-color="#FF931E" />
                  <stop offset="46.83%" stop-color="#F15A24" />
                  <stop offset="64.81%" stop-color="#456CE8" />
                  <stop offset="87.38%" stop-color="#000000" />
                </linearGradient>
              </defs>
              <rect x="0.5" y="0.5" width="249" height="249" rx="44.5"
                    fill="url(#da-favicon-grad)" />
              <path d="M135.409 42H124.828C124.166 42 123.631 42.5339 123.631 43.1954V59.9309C123.631 60.5923 123.089 61.1263 122.434 61.1263H103.355C102.868 61.1263 102.437 61.4132 102.254 61.8594L45.0955 198.477C44.6726 199.489 45.7338 200.493 46.7233 200.015C79.791 183.997 102.318 179.175 113.593 177.749L114.766 177.605C115.133 177.565 115.5 177.518 115.851 177.486L116.37 177.43C120.256 177.055 122.578 177.095 123.264 177.127C135.68 177.143 147.315 180.203 157.848 186.212C167.264 191.591 175.06 198.732 181.061 207.482C181.284 207.809 181.651 208 182.042 208H203.803C204.656 208 205.239 207.131 204.904 206.342L136.51 42.7332C136.327 42.2869 135.888 42 135.409 42ZM155.765 162.623C145.248 158.662 134.268 156.654 123.128 156.654C111.989 156.654 101.368 158.654 91.0343 162.615L78.8653 167.269L123.384 60.6561L167.87 167.181L155.765 162.623Z"
                    fill="white" />
            </svg>
            <span>${this.strings.poweredBy}</span>
          </div>`
        : null}

      <span class="sentinel" tabindex=${trapIndex} @focus=${this.focusFirst}></span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "deepagent-panel": DeepAgentPanel;
  }
}
